import { WorkflowOrganizerProps } from "interfaces/workflows/WorkflowOrganizerProps";
import { useState } from "react";
import { WorkflowsList } from "./WorkflowsList";
import WorkflowCreator from "./WorkflowCreator";
import { WorkflowViewer } from "./WorkflowViewer";
import type { WorkflowTemplate } from "models/workflows/WorkflowTemplate";

export const WorkflowOrganizer: React.FC<WorkflowOrganizerProps> = ({ ownerId }) => {
    const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>("");
    const [selectedTemplate, setSelectedTemplate] = useState<WorkflowTemplate | null>(null);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [listCollapsed, setListCollapsed] = useState<boolean>(false);

    const handleCreateNew = () => {
        setSelectedTemplate(null);
        setIsEditing(true);
        setListCollapsed(true);
    };

    const handleWorkflowSelectionClick = (workflowId: string) => {
        setSelectedWorkflowId(workflowId);
        setIsEditing(false);
    };

    const handleEditClick = (template: WorkflowTemplate) => {
        setSelectedTemplate(template);
        setIsEditing(true);
        setListCollapsed(true);
    };

    const isSidebarActive = isEditing || !!selectedWorkflowId;

    return (
        <div className="min-h-screen bg-gray-100 p-4">
            <div className="container mx-auto">
                <div className="bg-white p-6 rounded-lg shadow-lg">
                    <h1 className="text-2xl font-bold text-gray-800 mb-4">Workflow Organizer</h1>
                    <div className="flex gap-4">
                        {/* Sidebar — collapses to a narrow strip when creator/viewer is active */}
                        {isSidebarActive && listCollapsed ? (
                            <div className="flex-none">
                                <button
                                    type="button"
                                    onClick={() => setListCollapsed(false)}
                                    title="Show workflows list"
                                    className="flex flex-col items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 px-2 py-3 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                                    </svg>
                                    <span className="[writing-mode:vertical-rl] text-xs font-medium tracking-wide">Workflows</span>
                                </button>
                            </div>
                        ) : (
                            <div className={isSidebarActive ? "w-72 flex-none" : "flex-1"}>
                                {isSidebarActive && (
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-semibold text-gray-600">Workflows</span>
                                        <button
                                            type="button"
                                            onClick={() => setListCollapsed(true)}
                                            title="Collapse list"
                                            className="text-gray-400 hover:text-gray-600 focus:outline-none"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7M18 19l-7-7 7-7" />
                                            </svg>
                                        </button>
                                    </div>
                                )}
                                <WorkflowsList
                                    ownerId={ownerId}
                                    onCreateNew={handleCreateNew}
                                    onSelect={handleWorkflowSelectionClick}
                                    onEdit={handleEditClick}
                                />
                            </div>
                        )}

                        {/* Main content */}
                        {isSidebarActive && (
                            <div className="flex-1 min-w-0">
                                {isEditing && (
                                    <WorkflowCreator
                                        ownerId={ownerId}
                                        existingTemplate={selectedTemplate}
                                    />
                                )}
                                {!isEditing && selectedWorkflowId && (
                                    <WorkflowViewer
                                        workflowId={selectedWorkflowId}
                                        onEditClick={handleEditClick}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WorkflowOrganizer;